'use client';

import { useUserLeadsByPhone } from '@/hooks/useUserLeadsByPhone';
import { normalizePhoneNumber } from '@/utils/phone';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react'; // Added missing import for React

// Define ContactInfo type used in this form
interface ContactInfo {
  name?: string;
  phone: string;
  email?: string;
}

// Country options for phone number input
const COUNTRY_OPTIONS = [
  { code: '+32', flag: '🇧🇪', name: 'Belgium', shortName: 'BE' },
  { code: '+31', flag: '🇳🇱', name: 'Netherlands', shortName: 'NL' },
  { code: '+33', flag: '🇫🇷', name: 'France', shortName: 'FR' },
];

interface ContactFormProps {
  dictionary: any;
  disableSubmit?: boolean;
  onSubmit: (contactInfo: ContactInfo) => Promise<void>;
  showNameInput?: boolean;
  contactPrompt?: string;
  agencyId?: string;
  askEmail?: boolean;
  // Prefill for session-resume
  initialContactInfo?: Partial<ContactInfo>;
}

export default function ContactForm({
  dictionary,
  onSubmit,
  disableSubmit = false,
  showNameInput,
  contactPrompt,
  agencyId,
  askEmail,
  initialContactInfo,
}: ContactFormProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof ContactInfo, string>>>({});
  // Controlled inputs for contact form
  const [name, setName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_OPTIONS[0]); // Default to Belgium
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isCheckingLeads, setIsCheckingLeads] = useState(false);
  const [showLeadError, setShowLeadError] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0); // Add submission counter
  const [hookHasStarted, setHookHasStarted] = useState(false); // Track if hook has started

  // Hook to check lead count - only call when user submits
  const { leadCount, loading } = useUserLeadsByPhone(
    isCheckingLeads ? phone : null,  // Only call hook when isCheckingLeads is true
    isCheckingLeads ? (agencyId || null) : null, // Only call hook when isCheckingLeads is true
    showLeadError ? -1 : submissionCount // Don't re-run hook when showing error
  );

  // Stabilize the onSubmit function to prevent unnecessary useEffect runs
  const stableOnSubmit = React.useCallback(onSubmit, [onSubmit]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById('country-dropdown');
      const button = event.target as Element;
      if (dropdown && !dropdown.contains(button) && !button.closest('[data-country-selector]')) {
        dropdown.classList.add('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prefill
  React.useEffect(() => {
    try {
      if (initialContactInfo?.name) setName(initialContactInfo.name);

      if (initialContactInfo?.phone) {
        const phoneValue = initialContactInfo.phone;
        // Try to match the phone number with a country code
        const matchingCountry = COUNTRY_OPTIONS.find(country =>
          phoneValue.startsWith(country.code)
        );

        if (matchingCountry) {
          setSelectedCountry(matchingCountry);
          setPhone(phoneValue.substring(matchingCountry.code.length));
        } else {
          // If no country code match, default to Belgium and clean the number
          setSelectedCountry(COUNTRY_OPTIONS[0]);
          setPhone(phoneValue.replace(/[^0-9]/g, ''));
        }
      }

      if (initialContactInfo?.email) setEmail(initialContactInfo.email);
    } catch (_) {}
  }, [initialContactInfo]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Build full phone number with country code
    const fullPhoneNumber = selectedCountry.code + phone.replace(/[^0-9]/g, '');

    // Normalize phone number first
    const normalizedPhone = normalizePhoneNumber(fullPhoneNumber);
    const contactInfo: ContactInfo = { name: showNameInput ? name : undefined, phone: normalizedPhone, email: askEmail ? email : undefined };
    const newErrors: Partial<Record<keyof ContactInfo, string>> = {};

    if (!contactInfo.phone) {
      newErrors.phone = dictionary?.sellingForm?.requiredField || 'This field is required';
    } else if (!/^\+?[0-9\s\-\/()]{6,}$/.test(fullPhoneNumber)) {
      // Check original phone format for user feedback
      newErrors.phone = dictionary?.sellingForm?.invalidPhoneFormat || 'Invalid phone format';
    } else if (!/^\+(?:32|31|33)\d{9}$/.test(normalizedPhone)) {
      // Check if normalized phone matches supported country formats (BE/NL/FR)
      newErrors.phone = dictionary?.sellingForm?.invalidPhoneNumber || 'Please enter a valid phone number (Belgium, Netherlands, or France)';
    }

    if (askEmail) {
      if (!contactInfo.email) {
        newErrors.email = dictionary?.sellingForm?.requiredField || 'This field is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
        newErrors.email = dictionary?.sellingForm?.invalidEmail || 'Please enter a valid email address';
      }
    }

    // Add validation for name only when showNameInput is true
    if (showNameInput && !contactInfo.name) {
      newErrors.name = dictionary?.sellingForm?.requiredNameField || 'Name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    
    // Start checking lead count - DO NOT call onSubmit here
    setIsCheckingLeads(true);
    setSubmissionCount(prev => prev + 1); // Increment submission count
    setShowLeadError(false); // Reset error state for new submission
    setHookHasStarted(false); // Reset hook tracking for new submission
  };

  // Effect to handle lead count result
  React.useEffect(() => {
    const handleLeadCheckResult = async () => {
      // Track when hook starts
      if (isCheckingLeads && loading && !hookHasStarted) {
        setHookHasStarted(true);
        return;
      }
      
      // Only proceed when we have a definitive result (hook has started, not loading, and lead count is meaningful)
      if (isCheckingLeads && !loading && hookHasStarted && leadCount !== undefined) {
        // Reset hook tracking
        setHookHasStarted(false);
        
        // Hook has finished checking
        if (leadCount >= 3) {
          // Show error, don't proceed
          setIsCheckingLeads(false);
          setShowLeadError(true);
        } else {
          // Proceed with submission
          const fullPhoneNumber = selectedCountry.code + phone.replace(/[^0-9]/g, '');
          const normalizedPhone = normalizePhoneNumber(fullPhoneNumber);
          const contactInfo: ContactInfo = { name: showNameInput ? name : undefined, phone: normalizedPhone, email: askEmail ? email : undefined };
          setIsCheckingLeads(false);
          setShowLeadError(false);
          await stableOnSubmit(contactInfo);
        }
      }
    };

    handleLeadCheckResult();
  }, [isCheckingLeads, loading, leadCount, name, phone, email, askEmail, showNameInput, stableOnSubmit, submissionCount, hookHasStarted, selectedCountry]);

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full">
        <p
          className="text-base md:text-sm text-zinc-800 mb-4"
          style={{ whiteSpace: 'pre-line' }}
        >
          {contactPrompt || dictionary?.sellingForm?.contactPrompt || 'Please provide your contact details below.'}
        </p>

        {/* Contact Info Section */}
        <div className="space-y-3">
          {/* Name Input (Conditional) */}
          {showNameInput && (
            <div className="w-full">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder={dictionary?.sellingForm?.contactNameLabel || 'Your Name'}
                  className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>
              {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
            </div>
          )}

          {/* Email Input (Conditional) */}
          {askEmail && (
            <div className="w-full">
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder={dictionary?.sellingForm?.contactEmail || 'Email'}
                  className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
            </div>
          )}

          {/* Phone */}
          <div className="w-full">
            <div className="relative">
              {/* Country Selector Dropdown */}
              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                <div className="relative">
                  <button
                    type="button"
                    data-country-selector
                    className="flex items-center gap-1 px-2 py-1 text-sm bg-zinc-50 border border-zinc-200 rounded-md hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                    onClick={() => {
                      const dropdown = document.getElementById('country-dropdown');
                      dropdown?.classList.toggle('hidden');
                    }}
                  >
                    <span>{selectedCountry.flag}</span>
                    <span className="text-zinc-600">{selectedCountry.code}</span>
                    <ChevronDown className="w-3 h-3 text-zinc-500" />
                  </button>

                  {/* Dropdown Menu */}
                  <div
                    id="country-dropdown"
                    className="absolute top-full left-0 mt-1 w-32 bg-white border border-zinc-200 rounded-md shadow-lg hidden z-20"
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 first:rounded-t-md last:rounded-b-md"
                        onClick={() => {
                          setSelectedCountry(country);
                          const dropdown = document.getElementById('country-dropdown');
                          dropdown?.classList.add('hidden');
                        }}
                      >
                        <span>{country.flag}</span>
                        <span>{country.shortName}</span>
                        <span className="text-zinc-500">{country.code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <input
                type="tel"
                inputMode="tel"
                name="phone"
                placeholder={dictionary?.sellingForm?.contactPhone || 'Phone Number'}
                className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow pl-[90px] md:pl-22"
                value={phone}
                onChange={(e) => {
                  const enteredValue = e.target.value;

                  // Clean up the entered phone number
                  const digits = enteredValue.replace(/[^0-9]/g, '');

                  // Smart cleaning based on digit length and content
                  let cleanedDigits = digits;

                  if (digits.length >= 9) {
                    // Check for country code prefix (when user has typed enough digits)
                    const selectedCountryDigits = selectedCountry.code.slice(1); // Remove '+'
                    if (digits.startsWith(selectedCountryDigits)) {
                      // Remove the country code prefix
                      cleanedDigits = digits.slice(selectedCountryDigits.length);
                    } else if (digits.length === 10 && digits.startsWith('0')) {
                      // Remove leading 0 for 10-digit numbers (European local format)
                      cleanedDigits = digits.slice(1);
                    } else if (digits.length === 11) {
                      // Check for international format (32xxxxxxxxx, 31xxxxxxxxx, 33xxxxxxxxx)
                      if (digits.startsWith('32') || digits.startsWith('31') || digits.startsWith('33')) {
                        // Remove the country code from international format
                        cleanedDigits = digits.slice(2);
                      }
                    }
                  }

                  setPhone(cleanedDigits);
                }}
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {dictionary?.sellingForm?.phoneVerificationNotice || 'Note: This phone number will be verified.'}
            </p>
            {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
          </div>
        </div>

        {/* Lead Count Error Message */}
        {showLeadError && leadCount >= 3 && (
          <div className="w-full mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-800 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-amber-800">
                {dictionary?.sellingForm?.maxLeadsReachedMessage || 'You have already generated the maximum number of leads'}
              </p>
            </div>
          </div>
        )}
        
        {/* Submit Button */}
        <div className="mt-4">
          <button
            type="submit"
            disabled={disableSubmit || loading}
            className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            {loading ? (dictionary?.sellingForm?.checkingLeadsMessage || 'Checking...') : (dictionary?.sellingForm?.showValueButton || 'Show value')}
          </button>
        </div>
      </form>
    </>
  );
} 